import {
	AutoComplete,
	Button,
	Divider,
	Col,
	Input,
	Row,
	Table,
	Icon,
	Card,
	message,
	Popconfirm,
	Tooltip,
} from 'antd';
import React, { useEffect, useMemo, useState } from 'react';
import OrganizationModal from '../../components/organization/organizationModal.jsx';
import Highlight from '../../components/highlightComponent.js';
import iconStyle from '../../components/icon.cs.js';
import { getStringSorter, getIntSorter } from '../../utils/antUtils.js';
import { getPageSize2 } from '../../utils/paging.js';
import { useWindowHeight } from '../../hooks/useWindowHeight.js';
import {
	createAddOns,
	createOrganization,
	deleteOrganization,
	getOrganizations,
	restoreOrganization,
} from '../../utils/api.js';
import AddOnTags from '../../components/AddOnTags';
import { buildAddOns } from '../../utils/addOnsHelper.js';

const { Column } = Table;

const OrganizationList = ({}) => {
	const [organizations, setOrganizations] = useState([]);
	const [newOrgOpen, setNewOrgOpen] = useState(false);
	const [searchQuery, setSearchQuery] = useState('');

	const loadOrganizations = async () => {
		const result = (await getOrganizations()) || [];

		setOrganizations(result);
	};

	let data = organizations || [];
	const lowerCaseFilter = searchQuery ? searchQuery.toLowerCase() : '';
	const compare = (a, attr) => (String(a[attr]) || '').toLowerCase().indexOf(lowerCaseFilter) >= 0;

	data = !lowerCaseFilter
		? data
		: data.filter(
				(org) =>
					compare(org, 'name') ||
					compare(org, 'licenseKey') ||
					compare(org, 'allowedDatabase') ||
					compare(org, 'id')
		  );

	const filteredCount = data.length;
	const windowHeight = useWindowHeight();
	const pageSize = useMemo(() => getPageSize2(), [windowHeight]);

	const handleOrganizationModalSubmit = async (formData) => {
		const { addOns, ...formValues } = formData;

		const organizationBody = {
			name: formValues.name,
			address: formValues.address || '',
			zipCode: formValues.zipCode || '',
			phone: formValues.phone || '',
			city: formValues.city || '',
			allowedDatabase: formValues.databases.join('') || '',
			expiryDate: formValues.expiryDate.format('YYYY-MM-DD'),
			licenseKey: formValues.licenseKey,
			featureRMHandbook: (addOns && addOns.includes('RM_HANDBOOK')) || false,
			featureLegalComments: (addOns && addOns.includes('LEGAL_COMMENTS')) || false,
			featureELiterature: (addOns && addOns.includes('E_LITERATURE')) || false,
			featureCompliance: (addOns && addOns.includes('COMPLIANCE')) || false,
			sbtPlus: (addOns && addOns.includes('LEGAL_CASE_PLUS')) || false,
		};

		const handleAddOns = async (orgId) => {
			if (addOns && addOns.length) {
				const addOnData = addOns.map((addOn) => ({
					accountID: orgId,
					accountType: 'ORGANIZATION',
					type: addOn,
				}));

				const addOnResponse = await createAddOns(addOnData);
				if (!addOnResponse) {
					message.error('Misslyckades att skapa tillägg. Kontakta support.');
				}
			}
		};

		try {
			const updatedOrg = await createOrganization(organizationBody);
			if (updatedOrg.id) {
				message.success(`Kund ${updatedOrg.id} skapad`);
				await handleAddOns(updatedOrg.id);
				await loadOrganizations();
			}
		} catch (error) {
			message.error(`Kunde inte skapa kund`);
		}
	};

	const renderActions = (text, organization, index) => (
		<span
			style={{
				display: 'flex',
				flexWrap: 'nowrap',
				justifyContent: 'left',
				alignItems: 'center',
			}}
		>
			<a href={`/kund/${organization.id}`}>
				<Icon type='unordered-list' /> Detaljer
			</a>
			<Divider type='vertical' />
			{organization && organization.deletionDate && (
				<a onClick={() => handleOrganizationRestore(organization)} style={{ color: 'orange' }}>
					<Icon type='undo' /> Återställ
				</a>
			)}
			{organization && !organization.deletionDate && (
				<Popconfirm
					title={
						<span>
							Är du säker på att du vill radera <b>{organization.name}</b>?
						</span>
					}
					onConfirm={() => handleOrganizationDelete(organization)}
					okText='Ja'
					cancelText='Nej'
					okType='danger'
					okButtonProps={{ size: 'default' }}
					cancelButtonProps={{ size: 'default' }}
					placement='bottomRight'
					icon={<Icon type='delete' style={{ color: 'red' }} />}
				>
					<a style={{ color: 'red' }}>
						<Icon type='delete' /> Radera
					</a>
				</Popconfirm>
			)}
		</span>
	);

	const handleOrganizationDelete = async (organization) => {
		try {
			await deleteOrganization(organization.id);

			message.success(`Kund ${organization.id} markerad för radering`);
			await loadOrganizations();
		} catch (error) {
			message.error('Kunde inte radera kund');
		}
	};

	const handleOrganizationRestore = async (organization) => {
		try {
			await restoreOrganization(organization.id);

			message.success(`Kund ${organization.id} återställd`);
			await loadOrganizations();
		} catch (error) {
			message.error('Kunde inte återställa kund');
		}
	};

	useEffect(() => {
		if (organizations.length === 0) {
			loadOrganizations();
		}
	}, []);

	return (
		<div>
			<OrganizationModal
				mode={'create'}
				organization={{}}
				open={newOrgOpen}
				onCancel={() => setNewOrgOpen(false)}
				onSubmit={handleOrganizationModalSubmit}
			/>
			<Row style={{ marginBottom: 16 }}>
				<Col xl={8} lg={10} md={12} sm={14} xs={14}>
					<AutoComplete
						style={{ width: '100%' }}
						dataSource={[]}
						onSearch={(query) => setSearchQuery(query)}
						value={searchQuery}
						placeholder='Filtrera på kundnamn, licensnyckel eller databas...'
						size='large'
					>
						<Input suffix={<Icon type='search' />} />
					</AutoComplete>
				</Col>
				<Col lg={4} md={4} sm={4} xs={4}>
					<div style={{ padding: 8, fontSize: 16 }}>
						{filteredCount} {filteredCount === 1 ? 'träff' : 'träffar'}
					</div>
				</Col>
				<Col xl={12} lg={10} md={8} sm={6} xs={4}>
					<Button
						icon='plus'
						type='primary'
						size='large'
						onClick={() => setNewOrgOpen(true)}
						style={{ float: 'right' }}
					>
						Ny kund
					</Button>
				</Col>
			</Row>
			<Row>
				<Card style={{ width: '100%', padding: 0 }} bodyStyle={{ padding: '8px 12px' }}>
					<Table
						dataSource={data}
						size='small'
						pagination={{
							pageSize,
							position: 'bottom',
							size: 'default',
						}}
						bodyStyle={{ minHeight: '65vh' }}
						style={{
							background: '#fff',
							padding: 10,
							marginTop: 12,
							boxShadow: 'unset',
						}}
						rowClassName={(record) => {
							return record.deletionDate ? 'default-cursor warning-row' : 'default-cursor';
						}}
						scroll={{ x: 'max-content' }}
						loading={organizations.length === 0}
						rowKey={(record) => `KEY_${record.id}`}
						fixed
					>
						<Column
							title=' '
							width={15}
							fixed='left'
							render={(text, record) => {
								const deletionDate = record && record.deletionDate;

								const icon = deletionDate ? 'warning' : 'team';
								const style = deletionDate ? { ...iconStyle, color: 'orange' } : iconStyle;
								const title = deletionDate ? `Kund raderas ${deletionDate}` : null;
								return (
									<Tooltip placement='right' title={title}>
										<Icon type={icon} style={style} />
									</Tooltip>
								);
							}}
						/>
						<Column
							title='Id'
							width={80}
							dataIndex='id'
							key='id'
							fixed='left'
							sorter={getIntSorter('id')}
							render={(text) => <Highlight text={String(text)} query={lowerCaseFilter} />}
						/>
						<Column
							title='Namn'
							defaultSortOrder='ascend'
							dataIndex='name'
							key='name'
							fixed
							width={300}
							sorter={getStringSorter('name')}
							render={(text) => <Highlight text={text} query={lowerCaseFilter} maxWidth={290} />}
						/>
						<Column title=' ' width={20} render={(text) => ''} />
						<Column
							title='Licensnyckel'
							dataIndex='licenseKey'
							key='licenseKey'
							sorter={getStringSorter('licenseKey')}
							render={(text) => <Highlight text={text} query={lowerCaseFilter} />}
						/>
						<Column
							title='Licensdatum'
							dataIndex='expiryDate'
							key='expiryDate'
							sorter={getStringSorter('expiryDate')}
						/>
						<Column
							title='Databas'
							dataIndex='allowedDatabase'
							key='allowedDatabase'
							sorter={getStringSorter('allowedDatabase')}
							render={(text) => <Highlight text={text} query={lowerCaseFilter} />}
						/>
						<Column
							title='Plustjänster'
							dataIndex='addons'
							key='addons'
							render={(text, record) => <AddOnTags addOns={buildAddOns(record)} short />}
						/>
						<Column
							title='Åtgärder'
							key='actions'
							width={170}
							render={(text, record, index) => renderActions(text, record, index)}
							fixed='right'
						/>
					</Table>
				</Card>
			</Row>
		</div>
	);
};

export default OrganizationList;



// WEBPACK FOOTER //
// ./src/pages/organization/organizationList.jsx